'use client';

import { TicketGroup } from '@interfaces/ticketevolution/tikets';
import { useTicketsStore } from '@store/tickets-store';
import { useMemo } from 'react';
import { applyTicketFilterPolicy } from './ticket-filters';
import {
  buildZoneMedianPrices,
  getGreatDeals,
  sortTickets,
} from './ticket-ranking';

function useTicketFilterPolicy(selectedGroups: string[] | null) {
  const isOnlyElectronic = useTicketsStore((s) => s.isOnlyElectronic);
  const accessibleOnly = useTicketsStore((s) => s.accessibleOnly);
  const zoneBuckets = useTicketsStore((s) => s.zoneBuckets);
  const underPriceCap = useTicketsStore((s) => s.underPriceCap);
  const ticketsQuantity = useTicketsStore((s) => s.ticketsQuantity);
  const [min, max] = useTicketsStore((s) => s.priceRange);

  return useMemo(
    () => ({
      min,
      max,
      ticketsQuantity,
      isOnlyElectronic,
      zoneBuckets,
      accessibleOnly,
      underPriceCap,
      selectedGroups,
    }),
    [
      min,
      max,
      ticketsQuantity,
      isOnlyElectronic,
      zoneBuckets,
      accessibleOnly,
      underPriceCap,
      selectedGroups,
    ]
  );
}

export function useFilteredSortedTickets(list: TicketGroup[]) {
  const selectedTicketGroups = useTicketsStore((s) => s.selectedGroups);
  const sortBy = useTicketsStore((s) => s.sortby);
  const policy = useTicketFilterPolicy(selectedTicketGroups);

  return useMemo(() => {
    if (!list.length) {
      return {
        filtered: [] as TicketGroup[],
        greatDealIds: new Set<number>(),
        zoneMedians: buildZoneMedianPrices([]),
        resultCount: 0,
      };
    }

    const filtered = applyTicketFilterPolicy(list, policy);
    const zoneMedians = buildZoneMedianPrices(filtered);
    const greatDeals = getGreatDeals(filtered, zoneMedians, 6);
    const greatDealIds = new Set(greatDeals.map((t) => t.id));
    const sorted = sortTickets(filtered, sortBy, zoneMedians);

    return {
      filtered: sorted,
      greatDealIds,
      zoneMedians,
      resultCount: sorted.length,
    };
  }, [list, policy, sortBy]);
}

export function useMapTicketGroups(list: TicketGroup[]) {
  const policy = useTicketFilterPolicy(null);

  return useMemo(() => applyTicketFilterPolicy(list, policy), [list, policy]);
}
